import { getIn, useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';

import { type DivisionType } from '~/common/enums';
import { type ShareValues } from '@/containers/Finance/Invoices/hoa/shared/types/formTypes';

import relationDivisionLinesCtx from './RelationDivisionLines.context';

import { createEmptyShare } from './RelationDivisionLines.utils';
import { getShareRelationKey } from './RelationDivisionLine/RelationDivisionLine.utils';

type DivisionValuesInput = {
  divisionTypeName?: string;
  parentName?: string;
};

const useDivisionValues = (input?: DivisionValuesInput) => {
  const formik = useFormikContext();
  const parent = relationDivisionLinesCtx.parent.useContextOptional();

  const names = {
    divisionType: input?.divisionTypeName ?? parent?.divisionTypeName,
    parent: input?.parentName ?? parent?.name,
  };

  if (!names.divisionType) throw new Error('No divisionTypeName was given');
  if (!names.parent) throw new Error('No parentName was given');

  return {
    divisions: getIn(formik.values, names.parent) as ShareValues[],
    divisionType: getIn(formik.values, names.divisionType) as DivisionType | null,
  };
};

const useParent = relationDivisionLinesCtx.parent.useContext;

const useUnitRelationOptionsQuery = relationDivisionLinesCtx.relationAutocomplete.query.useContext;

const useUnitRelationOptionsQueryParams =
  relationDivisionLinesCtx.relationAutocomplete.params.useContext;

const useAddEmptyShare = () => {
  const { divisions } = useDivisionValues();

  const formik = useFormikContext();
  const parent = useParent();
  const unitRelationOptionsQuery = useUnitRelationOptionsQuery();

  const completelyDivided = (() => {
    const availableOptions = unitRelationOptionsQuery.selectors.getAvailableOptions();
    const blankLines = divisions.filter((division) => !getShareRelationKey(division)).length;

    return availableOptions.length <= blankLines;
  })();

  const { setFieldValue } = formik;

  const addEmptyShare = useCallback(() => {
    const nextDivisions = [...divisions, createEmptyShare()];

    setFieldValue(parent.name, nextDivisions);
  }, [divisions, parent.name, setFieldValue]);

  return useMemo(
    () => ({ addEmptyShare, disabled: parent.readOnly || completelyDivided }),
    [addEmptyShare, completelyDivided, parent.readOnly],
  );
};

export {
  useAddEmptyShare,
  useDivisionValues,
  useParent,
  useUnitRelationOptionsQuery,
  useUnitRelationOptionsQueryParams,
};
